import axios from 'axios';
import { env } from './env';
import { getAccessToken } from '@/api/client';

/**
 * Fire-and-forget POST to `/api/bi/telemetry/`.
 *
 * Telemetry is best-effort: we never want a logging failure to surface in
 * the UI, and we never want it to cascade into a render-time exception.
 * If the backend endpoint is missing (404) or the user is offline, the
 * promise still resolves — we just `console.warn` in dev for visibility.
 *
 * The endpoint is intentionally unauthenticated-tolerant: we still attach
 * the access token if present, but missing auth shouldn't drop a report
 * (the backend may accept anonymous error pings).
 */
export interface TelemetryPayload {
  kind: 'render_error' | 'unhandled_rejection' | 'manual';
  message: string;
  stack?: string;
  component?: string;
  url?: string;
  user_agent?: string;
  /** Free-form context bag for downstream filtering (e.g. `{ page: 'overview' }`). */
  extra?: Record<string, unknown>;
}

const ENDPOINT = '/api/bi/telemetry/';

let warnedAboutMissing = false;

export async function sendTelemetry(payload: TelemetryPayload): Promise<void> {
  try {
    const token = getAccessToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;

    await axios.post(
      `${env.apiBase}${ENDPOINT}`,
      {
        ...payload,
        url: payload.url ?? (typeof window !== 'undefined' ? window.location.href : ''),
        user_agent: payload.user_agent ?? (typeof navigator !== 'undefined' ? navigator.userAgent : ''),
        ts: new Date().toISOString(),
      },
      { headers, timeout: 5_000 },
    );
  } catch (err) {
    if (env.isDev && !warnedAboutMissing) {
      warnedAboutMissing = true;
      // eslint-disable-next-line no-console
      console.warn('[telemetry] dropped report (endpoint may be missing):', err);
    }
  }
}
